import useSWR from 'swr';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import React, { Fragment, useEffect, useLayoutEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

/** Components */
import Box from '@mui/material/Box';
import LoadMore from 'components/LoadMore';
import BrandCard from 'components/Brand/BrandCard';

/** Blocks */
import ListingPage from 'blocks/ListingPage';

/** Hooks */
import useAuth from 'hooks/useAuth';
import useFilters from 'hooks/useFilters';

/** Utils / Const */
import { bffFetcher, noOp } from 'utils/functions';
import { DEFAULT_BRANDS_LIMIT } from 'constants/listing-defaults';
import { filterUrlQueryParams, leadTimesList, minOrderList } from 'constants/category-filters';
import { useScrollToRef } from 'hooks/useScrollToRef';
import AdvertisingSelector from 'components/Listing/Cards/Advertising/AdvertisingSelector';

const brandNewInTabContentDefaultPropSx = {};

const brandNewInTabContentDefaultPropInitialBrandsData = {
  totalBrands: 0,
  brands: [],
};

const brandNewInTabContentDefaultPropHandleLimitChange = () => noOp;
const brandNewInTabContentDefaultPropHandleItemIdChange = () => noOp;

const BrandNewInTabContent = ({
  sx = brandNewInTabContentDefaultPropSx,
  handleLimitChange = brandNewInTabContentDefaultPropHandleLimitChange,
  initialBrandsData = brandNewInTabContentDefaultPropInitialBrandsData,
  itemId = '',
  handleItemIdChange = brandNewInTabContentDefaultPropHandleItemIdChange,
}) => {
  const limit = DEFAULT_BRANDS_LIMIT;
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [brands, setBrands] = useState(initialBrandsData?.brands || []);

  const { user } = useAuth();
  const shippingCountryCode = user?.shippingCountry;

  const [, setShouldScrollTo] = useScrollToRef();

  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.up('md'));

  useLayoutEffect(() => {
    setShouldScrollTo(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    onOffer,
    selectedMinOrder,
    selectedLeadTime,
    showLoadingState,
    selectedCountries,
    selectedCategories,
    selectedBrandValues,
    setShowLoadingState,
    selectedFlairs,
  } = useFilters();

  const queryParams = new URLSearchParams({
    ...(limit && { limit }),
    ...(offset && { offset }),
    ...(onOffer && { onOffer }),
    ...(selectedFlairs.length && { flairs: selectedFlairs }),
    ...(selectedMinOrder && { minOrder: minOrderList.find((o) => o.text === selectedMinOrder)?.value }),
    ...(selectedCategories.length && { category: [selectedCategories] }),
    ...(selectedCountries.length && { countries: selectedCountries.join(',').toUpperCase() }),
    ...(selectedBrandValues.length && { [filterUrlQueryParams.BRAND_VALUE]: selectedBrandValues.join(',') }),
    ...(selectedLeadTime && {
      [filterUrlQueryParams.LEAD_TIME_MAX_DAYS]: leadTimesList.filter(({ text }) => text === selectedLeadTime)[0]
        ?.maximumDays,
    }),
    ...(shippingCountryCode && { shippingCountryCode }),
  });

  const { isValidating: isLoadingMore, data: chunk } = useSWR(
    offset ? `/brands?sortBy=latest&${queryParams}` : null,
    bffFetcher
  );

  useEffect(() => {
    if (isEmpty(chunk?.brands)) return;
    setTotal(chunk.totalBrands || chunk.brands.length);
    setBrands((prev) => [...prev, ...chunk.brands]);
  }, [chunk?.totalBrands, chunk?.brands]);

  useEffect(() => {
    if (shippingCountryCode === 'guest' || !shippingCountryCode === 'user') {
      setTotal(0);
      setBrands([]);
      setOffset(0);
    }
  }, [shippingCountryCode]);

  useEffect(() => {
    if (showLoadingState) {
      setBrands([]);
      setTotal(0);
      setOffset(0);
    }
  }, [showLoadingState]);

  useEffect(() => {
    setTotal(initialBrandsData?.totalBrands || 0);
    setBrands(initialBrandsData?.brands || []);
    setOffset(0);
  }, [initialBrandsData]);

  const loadMoreBrands = () => {
    handleLimitChange();
    setOffset(brands.length);
  };

  // When changing the initial products and brands value, set loading state to false
  useEffect(() => {
    setShowLoadingState(false);
  }, [initialBrandsData, setShowLoadingState]);

  return (
    <ListingPage sx={{ ...sx, mx: isMediumScreen ? '-1rem' : '0' }}>
      <ListingPage.ItemContainer>
        {brands?.map((brand, index) => (
          <Fragment key={`brand-list-item-${brand?.id}`}>
            <ListingPage.Item
              id={brand?.id}
              key={`brand-id-${brand?.id}`}
              data-scroll-to-element={brand?.id === itemId ? 'scroll-reference-id' : null}
            >
              <BrandCard handleItemIdChange={handleItemIdChange} brand={brand} />
            </ListingPage.Item>
            <AdvertisingSelector index={index} listLength={brands?.length} />
          </Fragment>
        ))}
        {(isLoadingMore || showLoadingState) &&
          [1, 2, 3, 4, 5, 6, 7, 8].map((id) => (
            <ListingPage.Item key={`loading-brand-slug-${id}`}>
              <BrandCard isLoading />
            </ListingPage.Item>
          ))}
      </ListingPage.ItemContainer>
      <Box sx={{ my: 3, display: 'flex', justifyContent: 'center' }}>
        <LoadMore
          total={total}
          viewed={brands?.length}
          resource="brands"
          isLoading={isLoadingMore}
          onClickHandler={loadMoreBrands}
          progress={brands?.length ? (brands.length / total) * 100 : 0}
        />
      </Box>
    </ListingPage>
  );
};

BrandNewInTabContent.propTypes = {
  sx: PropTypes.shape({}),
  itemId: PropTypes.string,
  initialBrandsData: PropTypes.shape({
    totalBrands: PropTypes.number,
    brands: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  handleLimitChange: PropTypes.func,
  handleItemIdChange: PropTypes.func,
};

export default BrandNewInTabContent;
